import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import FlikSelect from '../../components/Select/FlikSelect';
import { isAdmin as isAdminUser } from '../../auth/roles';
import {
  getMyProfile,
  getAdminUsers,
  getAdminUsersCount,
  getAdminPremiumCount,
  getAdminRegistrations,
  logout,
} from '../../api/client';
import './AdminPage.css';

/**
 * Admin dashboard (route "/admin"). Reached from the "Admin" button on the
 * profile page, which only appears for users whose role is ADMIN. The page also
 * guards itself: it loads the signed-in profile and bounces non-admins back to
 * "/perfil", so typing the URL directly is not enough.
 *
 * Two sections:
 *  - "Resumen general": headline stat cards (total users, premium users) and a
 *    monthly registrations bar chart. Every figure without a wired endpoint is
 *    intentionally shown as "—".
 *  - "Usuarios": a searchable, role-filterable, paginated user table backed by
 *    GET /admin/users.
 *
 * Built for desktop (left rail) and mobile (top segmented control).
 */

const MONTHS_SHORT = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles' },
  { value: 'USER', label: 'Usuario' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'ADMIN', label: 'Administrador' },
];

const PAGE_SIZE = 10;
const EMPTY = '—';

/* ------------------------------ helpers -------------------------------- */

/** Groups thousands with a dot (es-ES style), independent of the ICU build. */
const nf = {
  format: (n) =>
    typeof n === 'number' && Number.isFinite(n)
      ? String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      : String(n),
};

/** Reads a numeric count from the many shapes a backend might answer with. */
function readCount(payload) {
  if (payload === null || payload === undefined) return null;
  if (typeof payload === 'number') return payload;
  if (typeof payload === 'string' && payload.trim() !== '' && !Number.isNaN(Number(payload))) {
    return Number(payload);
  }
  if (typeof payload === 'object') {
    const v = payload.count ?? payload.total ?? payload.value ?? payload.totalElements;
    return typeof v === 'number' ? v : null;
  }
  return null;
}

/** A count formatted for display, or the em-dash placeholder. */
const fmtCount = (v) => (v === null || v === undefined ? EMPTY : nf.format(v));

/**
 * Normalises the registrations payload into a fixed 12-slot array of counts
 * (index 0 = January). Accepts an array of { month, count|total|value }, a plain
 * map { "1": 10, ... }, or an array of raw numbers.
 */
function normalizeRegistrations(payload) {
  const slots = new Array(12).fill(0);
  if (!payload) return slots;

  if (Array.isArray(payload)) {
    payload.forEach((item, i) => {
      if (typeof item === 'number') {
        if (i < 12) slots[i] = item;
        return;
      }
      if (item && typeof item === 'object') {
        const month = Number(item.month ?? item.key ?? item.label);
        const count = Number(item.count ?? item.total ?? item.value ?? item.registrations ?? 0);
        if (month >= 1 && month <= 12) slots[month - 1] = count || 0;
      }
    });
    return slots;
  }

  if (typeof payload === 'object') {
    Object.entries(payload).forEach(([k, v]) => {
      const month = Number(k);
      if (month >= 1 && month <= 12) slots[month - 1] = Number(v) || 0;
    });
  }
  return slots;
}

function fullNameOf(u) {
  return [u.name, u.surname].filter(Boolean).join(' ').trim();
}

function initialsOf(u) {
  const full = fullNameOf(u);
  if (full) {
    const parts = full.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }
  return (u.username?.[0] || u.email?.[0] || '?').toUpperCase();
}

function roleLabel(role) {
  const norm = typeof role === 'string' ? role.replace(/^ROLE_/i, '').toUpperCase() : '';
  const found = ROLE_OPTIONS.find((r) => r.value === norm);
  return found ? found.label : (role || EMPTY);
}

function roleClass(role) {
  const norm = typeof role === 'string' ? role.replace(/^ROLE_/i, '').toUpperCase() : '';
  if (norm === 'ADMIN') return 'AdminBadge AdminBadge--admin';
  if (norm === 'PREMIUM') return 'AdminBadge AdminBadge--premium';
  return 'AdminBadge';
}

const NAV = [
  { id: 'overview', label: 'Resumen general', icon: GridIcon },
  { id: 'users', label: 'Usuarios', icon: UsersIcon },
];

const SOON = ['Moderación', 'Contenido', 'Rutas', 'Ajustes'];

/* ------------------------------ component ------------------------------ */

function AdminPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null); // null = checking
  const [tab, setTab] = useState('overview');

  // Gate the page on the real role from GET /users/me.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await getMyProfile();
        if (!alive) return;
        if (isAdminUser(me)) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
          navigate('/perfil', { replace: true });
        }
      } catch {
        if (!alive) return;
        setAuthorized(false);
        navigate('/perfil', { replace: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/authenticate?tab=login', { replace: true });
  };

  if (authorized !== true) {
    return (
      <div className="Admin Admin--loading">
        <FlikLogo height={22} />
        <p>Comprobando permisos…</p>
      </div>
    );
  }

  return (
    <div className="Admin">
      {/* ---- Desktop left rail ---- */}
      <aside className="Admin__sidebar">
        <button
          type="button"
          className="Admin__logoBtn"
          onClick={() => navigate('/')}
          aria-label="Ir al inicio"
        >
          <FlikLogo height={22} />
        </button>
        <span className="Admin__railTag">Panel de administración</span>

        <nav className="Admin__nav" aria-label="Secciones de administración">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`Admin__navItem${tab === id ? ' is-active' : ''}`}
              aria-current={tab === id ? 'page' : undefined}
              onClick={() => setTab(id)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
          {SOON.map((label) => (
            <span key={label} className="Admin__navItem Admin__navItem--soon" aria-disabled="true">
              <DotIcon />
              <span>{label}</span>
              <em>Próximamente</em>
            </span>
          ))}
        </nav>

        <div className="Admin__railFoot">
          <button type="button" className="Admin__railLink" onClick={() => navigate('/perfil')}>
            ← Volver al perfil
          </button>
          <button type="button" className="Admin__railLink Admin__railLink--muted" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* ---- Body ---- */}
      <div className="Admin__body">
        {/* Mobile header + segmented tabs */}
        <header className="Admin__mobileBar">
          <button
            type="button"
            className="Admin__logoBtn"
            onClick={() => navigate('/perfil')}
            aria-label="Volver al perfil"
          >
            <FlikLogo height={18} />
          </button>
          <button type="button" className="Admin__mobileLogout" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </header>

        <div className="Admin__tabs" role="tablist" aria-label="Secciones">
          {NAV.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`Admin__tab${tab === id ? ' is-active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <main className="Admin__main">
          {tab === 'overview' ? <OverviewSection /> : <UsersSection />}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------ overview ------------------------------- */

function OverviewSection() {
  const year = new Date().getFullYear();
  const [total, setTotal] = useState(null);
  const [premium, setPremium] = useState(null);
  const [reg, setReg] = useState(null); // 12-slot array or null while loading
  const [regError, setRegError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const months = Array.from({ length: 12 }, (_, i) => i + 1);
      const [totalRes, premiumRes, regRes] = await Promise.allSettled([
        getAdminUsersCount(),
        getAdminPremiumCount(),
        getAdminRegistrations({ year, months }),
      ]);
      if (!alive) return;
      setTotal(totalRes.status === 'fulfilled' ? readCount(totalRes.value) : null);
      setPremium(premiumRes.status === 'fulfilled' ? readCount(premiumRes.value) : null);
      if (regRes.status === 'fulfilled') {
        setReg(normalizeRegistrations(regRes.value));
      } else {
        setRegError(true);
        setReg(new Array(12).fill(0));
      }
    })();
    return () => {
      alive = false;
    };
  }, [year]);

  const premiumShare =
    total && premium != null && total > 0 ? Math.round((premium / total) * 100) : null;

  return (
    <section className="Admin__section" aria-label="Resumen general">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Resumen general</h1>
          <p className="Admin__subtitle">Vista rápida del estado de la plataforma.</p>
        </div>
        <span className="Admin__pill">{year}</span>
      </div>

      <div className="Admin__stats">
        <StatCard
          label="Usuarios registrados"
          value={fmtCount(total)}
          hint="Total en la plataforma"
          tone="ink"
        />
        <StatCard
          label="Usuarios premium"
          value={fmtCount(premium)}
          hint={premiumShare != null ? `${premiumShare}% del total` : 'Suscripciones activas'}
          tone="gold"
        />
        <StatCard label="Ingresos" value={EMPTY} hint="Sin datos" tone="dark" />
        <StatCard label="Reportes activos" value={EMPTY} hint="Sin datos" tone="ink" />
      </div>

      <div className="Admin__overviewGrid">
        <RegistrationsCard year={year} data={reg} error={regError} />

        <div className="AdminCard AdminCard--muted">
          <h2 className="AdminCard__eyebrow">Lista de moderación</h2>
          <div className="Admin__empty">
            <span className="Admin__emptyMark">{EMPTY}</span>
            <p>Sin datos disponibles</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, hint, tone }) {
  return (
    <div className={`StatCard StatCard--${tone}`}>
      <span className="StatCard__label">{label}</span>
      <strong className="StatCard__value">{value}</strong>
      <span className="StatCard__hint">{hint}</span>
    </div>
  );
}

function RegistrationsCard({ year, data, error }) {
  const loading = data === null;
  const slots = data || new Array(12).fill(0);
  const max = Math.max(1, ...slots);
  const totalYear = slots.reduce((a, b) => a + b, 0);
  const peakIdx = slots.indexOf(Math.max(...slots));

  return (
    <div className="AdminCard">
      <div className="AdminCard__head">
        <div>
          <h2 className="AdminCard__eyebrow">Altas / registros</h2>
          <p className="AdminCard__lead">
            {loading
              ? 'Cargando…'
              : error
              ? 'Sin datos disponibles'
              : `${nf.format(totalYear)} registros en ${year}`}
          </p>
        </div>
      </div>

      <div className="Chart" role="img" aria-label={`Registros mensuales de ${year}`}>
        {slots.map((count, i) => {
          const pct = loading ? 0 : Math.round((count / max) * 100);
          const isPeak = !loading && !error && totalYear > 0 && i === peakIdx;
          return (
            <div className="Chart__col" key={MONTHS_SHORT[i]}>
              <div className="Chart__barWrap">
                <span className="Chart__count">{loading || error ? '' : count || ''}</span>
                <span
                  className={`Chart__bar${isPeak ? ' is-peak' : ''}`}
                  style={{ height: `${Math.max(pct, count > 0 ? 6 : 2)}%` }}
                />
              </div>
              <span className="Chart__label">{MONTHS_SHORT[i]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------- users --------------------------------- */

function UsersSection() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [page, setPage] = useState(0);

  const [data, setData] = useState(null); // Page payload
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debounce the free-text search.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await getAdminUsers({ search, role, page, size: PAGE_SIZE });
        if (!alive) return;
        setData(res || {});
      } catch (err) {
        if (!alive) return;
        setError('No hemos podido cargar los usuarios.');
        setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [search, role, page]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.content || data.items || data.users || [];
  }, [data]);

  const totalPages = data?.totalPages ?? (rows.length < PAGE_SIZE && page === 0 ? 1 : undefined);
  const totalElements = data?.totalElements;
  const currentPage = data?.number ?? page;
  const hasPrev = currentPage > 0;
  const hasNext =
    totalPages != null ? currentPage + 1 < totalPages : rows.length === PAGE_SIZE;

  const onRoleChange = (value) => {
    setRole(value);
    setPage(0);
  };

  return (
    <section className="Admin__section" aria-label="Usuarios">
      <div className="Admin__sectionHead">
        <div>
          <h1 className="Admin__title">Usuarios</h1>
          <p className="Admin__subtitle">
            {totalElements != null
              ? `${nf.format(totalElements)} usuarios registrados`
              : 'Busca y filtra los usuarios de la plataforma.'}
          </p>
        </div>
      </div>

      <div className="Admin__toolbar">
        <div className="Admin__searchBox">
          <SearchIcon />
          <input
            type="search"
            className="Admin__search"
            placeholder="Buscar por usuario, nombre o email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Buscar usuarios"
          />
        </div>
        <FlikSelect
          variant="box"
          value={role}
          onChange={onRoleChange}
          options={ROLE_OPTIONS}
          ariaLabel="Filtrar por rol"
        />
      </div>

      <div className="AdminCard AdminCard--table">
        <div className="AdminTable" role="table">
          <div className="AdminTable__head" role="row">
            <span role="columnheader">Usuario</span>
            <span role="columnheader" className="AdminTable__colEmail">Email</span>
            <span role="columnheader" className="AdminTable__colRole">Rol</span>
            <span role="columnheader" className="AdminTable__colDate">Alta</span>
          </div>

          {loading ? (
            <div className="AdminTable__state">Cargando usuarios…</div>
          ) : error ? (
            <div className="AdminTable__state AdminTable__state--error">{error}</div>
          ) : rows.length === 0 ? (
            <div className="AdminTable__state">No se han encontrado usuarios.</div>
          ) : (
            rows.map((u, i) => (
              <div className="AdminTable__row" role="row" key={u.id ?? u.username ?? u.email ?? i}>
                <div className="AdminTable__user" role="cell">
                  <span className="AdminTable__avatar" aria-hidden="true">{initialsOf(u)}</span>
                  <span className="AdminTable__userText">
                    <strong>{fullNameOf(u) || u.username || '—'}</strong>
                    <em>@{u.username || '—'}</em>
                  </span>
                </div>
                <span className="AdminTable__colEmail AdminTable__email" role="cell">
                  {u.email || EMPTY}
                </span>
                <span className="AdminTable__colRole" role="cell">
                  <span className={roleClass(u.role || (Array.isArray(u.roles) ? u.roles[0] : u.userType))}>
                    {roleLabel(u.role || (Array.isArray(u.roles) ? u.roles[0] : u.userType))}
                  </span>
                </span>
                <span className="AdminTable__colDate AdminTable__date" role="cell">
                  {formatDate(u.createdAt || u.registeredAt || u.joinedAt)}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="AdminTable__foot">
          <span className="AdminTable__pageInfo">
            {totalPages != null
              ? `Página ${currentPage + 1} de ${Math.max(totalPages, 1)}`
              : `Página ${currentPage + 1}`}
          </span>
          <div className="AdminTable__pager">
            <button
              type="button"
              className="AdminPager__btn"
              disabled={!hasPrev || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              className="AdminPager__btn"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ------------------------------- icons --------------------------------- */

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="4" y="4" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="9" cy="8" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M16 5.2A3.4 3.4 0 0 1 16 11m2 9c0-2.6-1.3-4.5-3.3-5.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function DotIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default AdminPage;
